/**
 * The AI review surfaces' documents, filled in from the matter's own file
 * record (VALE-1250).
 *
 * Both review surfaces list their documents from `preview-changes`' `FileDiff`,
 * which carries four things: the file id, the original filename, the proposed
 * type and the certified-copy flag. That is enough for a row and not enough for
 * the drawer — no size, no MIME type, no upload date and no note — so the
 * drawer used to drop its whole details grid rather than render four blank rows
 * (`hideFields`, VALE-977).
 *
 * The files themselves were never the problem. They are ordinary saved files
 * with real ids on a real matter, which is why both surfaces already download
 * them by id. So rather than widening `FileDiff` into a second copy of the file
 * record, this reads the record itself — `useMatterFiles`, the same query and
 * the same cache the Documents page reads — and fills the gaps from it.
 *
 * Reading the record rather than restating it is what makes the note work at
 * all: `useUpdateFile` invalidates that list, so a note saved here re-derives
 * from the same row the Documents page renders, and the two cannot disagree
 * about what the note says.
 */

'use client';

import { useCallback, useMemo } from 'react';
import { useMatterFiles } from '@/lib/services/files/hooks';
import { useUpdateFile } from '@/lib/services/files/mutations';
import { toDocumentItem } from '@/components/matter-details/tabs/documents-bucketing';
import type { DocumentItem } from '@/components/documents/v2';

export interface ReviewDocumentRecords {
  /**
   * Fill one review document's drawer detail from the matter's file record.
   *
   * Takes the item the surface already built rather than the review shape, so
   * this knows nothing about either surface's own mapping and cannot drift from
   * it. Returns the item untouched when the record is not there — while the
   * query is in flight, or on a surface with no matter to read.
   */
  resolve: (base: DocumentItem) => DocumentItem;
  /**
   * Save the user's note against the file, or clear it with an empty string.
   *
   * `undefined` where there is no matter to write to, which is what makes the
   * drawer's Note row render read-only rather than as a control that silently
   * does nothing.
   */
  onNotesChange?: (documentId: number, notes: string) => Promise<void>;
  /**
   * Mark the file a certified copy, or unmark it.
   *
   * The fallback for a review surface that offers no handler of its own. Its
   * document list already renders the `Mark as certified copy` item
   * unconditionally, so without this the reviewer meets a menu item that
   * silently does nothing beside a drawer row that admits it cannot be
   * changed — the two disagreeing about the same flag. `undefined` with no
   * matter, where neither can write and both say so.
   */
  onCertifiedCopyChange?: (
    documentId: number,
    isCertified: boolean
  ) => Promise<void>;
}

export function useReviewDocumentRecords(
  matterId?: number
): ReviewDocumentRecords {
  // `0` disables the query inside `useMatterFiles` (`enabled: !!matterId`),
  // which is the whole of the no-matter case — there is nothing to read and
  // nothing to write, and `resolve` then hands every item straight back.
  const { data } = useMatterFiles(matterId ?? 0);
  const updateFile = useUpdateFile();

  /*
    Through `toDocumentItem`, the Documents tab's own mapper, rather than a
    second reading of `MatterFile` here. It already owns the two derivations
    that are easy to get wrong — `fileSizeKb` is kilobytes, and `notes` has had
    its user-note-over-analysis-sentence precedence resolved backend-side — so a
    change to either reaches this surface without being re-applied.
  */
  const byId = useMemo(() => {
    const records = new Map<number, DocumentItem>();
    for (const file of data?.files ?? []) {
      records.set(file.id, toDocumentItem(file));
    }
    return records;
  }, [data]);

  const resolve = useCallback(
    (base: DocumentItem): DocumentItem => {
      const record = byId.get(base.id);
      if (!record) return base;

      /*
        **Where the record has an answer, the record is the answer.** Not a
        merge of two opinions — the file row IS the fact, and the review shape's
        copy of it is a projection of one `preview-changes` response taken at
        the moment the extract was prepared. Preferring the projection is how
        the drawer ends up reporting a certified-copy flag or a note that the
        reader changed a moment ago on the same screen.

        This is also what makes the writes below correct without any local
        optimistic state: `useUpdateFile` invalidates this list, so a saved
        value arrives by re-derivation from the row it was written to. It is the
        Documents page's own behaviour, which is the surface these two are
        supposed to be converging on.
      */
      return {
        ...base,
        notes: record.notes,
        sizeBytes: record.sizeBytes,
        uploadedAt: record.uploadedAt,
        mimeType: record.mimeType,
        isCertifiedCopy: record.isCertifiedCopy,
        originalFilename: record.originalFilename,
        /*
          Why Vale could not read the document, when it could not. Not one of
          the detail rows — it draws the drawer's own notice, above a preview
          that still renders. Carried because the alternative is the two
          surfaces disagreeing about the same file: the Documents page tells a
          reader the analysis failed and the review surface, which is precisely
          where they are judging that analysis, does not.
        */
        analysisFailureReasonCode: record.analysisFailureReasonCode
        /*
          `name`, `fileType`, `category` and `typeSlug` are deliberately NOT
          taken from the record. They are the surface's own display mapping,
          and on one of them the two genuinely differ: `toDocumentItem` falls
          back to `'pdf'` for a file whose name carries no extension, which on a
          review screen is a guess presented as a fact (see
          `toReviewDocumentItem`).
        */
      };
    },
    [byId]
  );

  const saveNotes = useCallback(
    async (documentId: number, notes: string) => {
      if (!matterId) return;
      await updateFile.mutateAsync({
        matterId,
        fileId: documentId,
        data: { notes }
      });
    },
    [matterId, updateFile]
  );

  /*
    Awaited rather than fired and forgotten, even though the prop it satisfies
    is `void`-returning: the attribute grid awaits whatever `onSave` hands back
    and must not resolve until the value is in the record the row re-derives
    from (`components/attributes/CLAUDE.md` §6 / VALE-709). A rejection
    propagating is what gives the row its refusal ring rather than a silent
    no-op, so it is deliberately not caught here — the mutation raises its own
    toast alongside.
  */
  const saveCertifiedCopy = useCallback(
    async (documentId: number, isCertified: boolean) => {
      if (!matterId) return;
      await updateFile.mutateAsync({
        matterId,
        fileId: documentId,
        data: { isCertifiedCopy: isCertified }
      });
    },
    [matterId, updateFile]
  );

  return useMemo(
    () => ({
      resolve,
      onNotesChange: matterId ? saveNotes : undefined,
      onCertifiedCopyChange: matterId ? saveCertifiedCopy : undefined
    }),
    [resolve, matterId, saveNotes, saveCertifiedCopy]
  );
}
